import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, Bug, Lightbulb, HelpCircle, Heart } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = { bug: Bug, idea: Lightbulb, question: HelpCircle, praise: Heart };
const TYPE_COLORS = { bug: 'text-red-400', idea: 'text-amber-400', question: 'text-blue-400', praise: 'text-pink-400' };
const STATUS_COLORS = { new: 'border-blue-500/30 text-blue-400', in_review: 'border-amber-500/30 text-amber-400', resolved: 'border-emerald-500/30 text-emerald-400' };
const SEVERITY_COLORS = { low: 'text-muted-foreground', medium: 'text-amber-400', high: 'text-orange-400', critical: 'text-red-400' };

export default function AdminFeedbackPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => { load(); }, [statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {}; if (statusFilter) params.status = statusFilter;
      const res = await adminAPI.feedback(params);
      setItems(res.data.items || []); setStats(res.data.stats || {});
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try { await adminAPI.updateFeedback(id, { status }); toast.success(`Marked as ${status}`); load(); if (selected?.id === id) setSelected(prev => ({ ...prev, status })); } catch { toast.error('Failed'); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    try { await adminAPI.updateFeedback(selected.id, { admin_notes: adminNotes }); toast.success('Notes saved'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px]" data-testid="admin-feedback">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Feedback</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">User-submitted bugs, ideas, and questions</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1.5 text-[10px]">
            <Badge variant="outline" className="border-blue-500/30">New: {stats.new || 0}</Badge>
            <Badge variant="outline" className="border-amber-500/30">Review: {stats.in_review || 0}</Badge>
            <Badge variant="outline" className="border-emerald-500/30">Done: {stats.resolved || 0}</Badge>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[110px] h-7 text-[10px]"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="in_review">In Review</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No feedback yet.</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const TypeIcon = TYPE_ICONS[item.type] || MessageSquare;
            return (
              <Card key={item.id} className="cursor-pointer hover:border-primary/20 transition-colors" onClick={() => { setSelected(item); setAdminNotes(item.admin_notes || ''); }}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <TypeIcon className={`w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0 ${TYPE_COLORS[item.type] || ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                        <Badge variant="outline" className={`text-[9px] sm:text-[10px] ${STATUS_COLORS[item.status] || ''}`}>{item.status}</Badge>
                        <Badge variant="outline" className="text-[9px]">{item.type}</Badge>
                        <span className={`text-[9px] sm:text-[10px] ${SEVERITY_COLORS[item.severity] || ''}`}>{item.severity}</span>
                        <span className="text-[9px] text-muted-foreground sm:ml-auto">{new Date(item.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-xs sm:text-sm mt-1.5 line-clamp-2">{item.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{item.user_name} ({item.user_email?.split('@')[0]})</p>
                    </div>
                    <div className="hidden sm:flex gap-1 flex-shrink-0">
                      {item.status === 'new' && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); updateStatus(item.id, 'in_review'); }}>Review</Button>}
                      {item.status === 'in_review' && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); updateStatus(item.id, 'resolved'); }}>Resolve</Button>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader><DialogTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>Feedback Detail</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className={STATUS_COLORS[selected.status]}>{selected.status}</Badge>
                <Badge variant="outline">{selected.type}</Badge>
                <Badge variant="outline" className={SEVERITY_COLORS[selected.severity]}>{selected.severity}</Badge>
              </div>
              <p className="text-xs sm:text-sm">{selected.message}</p>
              <div className="text-[10px] sm:text-xs text-muted-foreground space-y-0.5">
                <p>From: {selected.user_name} ({selected.user_email})</p>
                <p>Date: {new Date(selected.created_at).toLocaleString()}</p>
                {selected.page_url && <p>Page: {selected.page_url}</p>}
              </div>
              <div>
                <label className="text-xs font-medium">Admin Notes</label>
                <Textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Notes..." className="mt-1 text-xs" rows={3} />
                <Button size="sm" className="mt-2 h-7 text-xs" onClick={saveNotes}>Save Notes</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selected.status !== 'in_review' && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => updateStatus(selected.id, 'in_review')}>Mark In Review</Button>}
                {selected.status !== 'resolved' && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => updateStatus(selected.id, 'resolved')}>Mark Resolved</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
